import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface NavbarProps {
  onConsultClick: () => void;
}

export default function Navbar({ onConsultClick }: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path;

  const navLinks = [
    { to: '/', label: '首頁' },
    { to: '/about', label: '關於我們' },
    { to: '/services', label: '服務項目' },
    { to: '/faq', label: '常見問題' },
  ];

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#0A192F] shadow-lg shadow-[#00D4FF]/10' : 'bg-[#0A192F]/95 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 cursor-pointer min-w-0 flex-shrink-0">
          <img 
            src="https://static.readdy.ai/image/ac888b511a221428483efbe2f4452036/52062b15afd5a25e63638b57c5108876.png" 
            alt="全鏈獲客整合行銷 Logo" 
            className="h-10 sm:h-12 w-auto object-contain"
          />
        </Link>

        {/* 桌面版選單 */}
        <div className="hidden md:flex items-center gap-6 lg:gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`transition-colors font-medium whitespace-nowrap cursor-pointer text-sm lg:text-base ${
                isActive(link.to) ? 'text-[#00D4FF]' : 'text-gray-300 hover:text-[#00D4FF]'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <button
            onClick={onConsultClick}
            className="bg-gradient-to-r from-[#00D4FF] to-[#0099CC] text-white px-5 lg:px-7 py-2.5 sm:py-3 rounded-full hover:from-[#00E5FF] hover:to-[#00AADD] hover:shadow-lg hover:shadow-[#00D4FF]/50 transition-all font-semibold whitespace-nowrap cursor-pointer text-sm lg:text-base"
          >
            預約展示 (Book Demo)
          </button>
        </div>

        {/* 手機版漢堡按鈕 */}
        <button
          className="md:hidden w-10 h-10 flex items-center justify-center text-[#00D4FF] cursor-pointer flex-shrink-0"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="開啟選單"
        >
          <i className={`text-2xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {/* 手機版下拉選單 */}
      {menuOpen && (
        <div className="md:hidden bg-[#0A192F] border-t border-[#00D4FF]/20 shadow-lg">
          <div className="px-4 py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-4 py-3 rounded-lg font-medium text-base transition-colors cursor-pointer ${
                  isActive(link.to)
                    ? 'bg-[#00D4FF]/20 text-[#00D4FF]'
                    : 'text-gray-300 hover:bg-[#00D4FF]/10 hover:text-[#00D4FF]'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <button
              onClick={() => { setMenuOpen(false); onConsultClick(); }}
              className="mt-2 bg-gradient-to-r from-[#00D4FF] to-[#0099CC] text-white px-6 py-3 rounded-full hover:from-[#00E5FF] hover:to-[#00AADD] transition-all font-semibold whitespace-nowrap cursor-pointer text-base"
            >
              預約展示 (Book Demo)
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}